import { ComboboxData, MantineSize } from '@mantine/core';
import { BenefitType, MajorMedicalCarrierDto } from '@zorro/clients';
import { Autocomplete } from '@zorro/design-system/v2/Autocomplete';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Select } from '@zorro/design-system/v2/Select';
import {
  dentalCarriersDatabase,
  visionCarriersDatabase,
} from '@zorro/shared/databases';
import { FormErrorMessage } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useEffect, useMemo, useState } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

import { useAnalytics } from '../Analytics';

function getMajorMedicalCarrierOptions(
  externalData?: string[],
  data?: MajorMedicalCarrierDto[]
): ComboboxData {
  if (externalData) {
    return externalData;
  }

  if (data) {
    const carriers = data.map(({ name }) => name);
    const uniqueCarriers = new Set(carriers);
    return [...uniqueCarriers];
  }

  return [];
}

function getDentalCarrierOptions(): ComboboxData {
  const carrierNames = dentalCarriersDatabase.map(
    (carrier) => carrier.carrierName
  );
  return [...new Set(carrierNames)];
}

function getVisionCarrierOptions(): ComboboxData {
  const carrierNames = visionCarriersDatabase.map(
    (carrier) => carrier.carrierName
  );
  return [...new Set(carrierNames)];
}

// If a new benefit type becomes available, implement the function to return the correct carrier options
const BENEFIT_CARRIER_OPTION_MAPPERS: Record<
  BenefitType,
  (carriers?: string[], data?: MajorMedicalCarrierDto[]) => ComboboxData
> = {
  [BenefitType.SUPPLEMENTAL_DENTAL]: () => getDentalCarrierOptions(),
  [BenefitType.SUPPLEMENTAL_COMBINED_DENTAL_VISION]: () =>
    getDentalCarrierOptions(),
  [BenefitType.MAJOR_MEDICAL]: (carriers, data) =>
    getMajorMedicalCarrierOptions(carriers, data),
  [BenefitType.SUPPLEMENTAL_VISION]: () => getVisionCarrierOptions(),
  [BenefitType.SUPPLEMENTAL_ACCIDENT]: () => [],
  [BenefitType.SUPPLEMENTAL_ACCIDENTAL_DEATH_AND_DISMEMBERMENT]: () => [],
  [BenefitType.SUPPLEMENTAL_CRITICAL_ILLNESS]: () => [],
  [BenefitType.SUPPLEMENTAL_FLEXIBLE_SPENDING_ACCOUNT]: () => [],
  [BenefitType.SUPPLEMENTAL_HOSPITAL]: () => [],
  [BenefitType.SUPPLEMENTAL_LIFE]: () => [],
  [BenefitType.SUPPLEMENTAL_OTHER]: () => [],
};

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  size?: MantineSize;
  shouldShowError?: boolean;
  carriers?: string[];
  shouldShowNotListedCheckbox?: boolean;
  benefitType?: BenefitType;
  analyticsPayload?: object;
};

export function CarrierInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  placeholder = 'Carrier',
  label = 'Carrier',
  size = 'lg',
  isRequired,
  isDisabled,
  isEditable = false,
  shouldShowError = false,
  carriers,
  shouldShowNotListedCheckbox = true,
  benefitType = BenefitType.MAJOR_MEDICAL,
  analyticsPayload,
  ...formProps
}: Props<TFieldValues, TName>) {
  const isExternalData = Boolean(carriers);

  const [isEditMode, setIsEditMode] = useState<boolean>(isEditable);
  const { sendEvent } = useAnalytics();

  const {
    field,
    formState: { errors },
  } = useController(formProps);

  const { data: majorMedicalCarriersData, isLoading } = useMonolithQuery({
    method: 'majorMedicalCarriersControllerGetCarriers',
    params: [],
    enabled: !isExternalData && benefitType === BenefitType.MAJOR_MEDICAL,
  });

  const carrierOptions = useMemo(() => {
    const mapper = BENEFIT_CARRIER_OPTION_MAPPERS[benefitType];
    return mapper
      ? mapper(carriers, majorMedicalCarriersData)
      : getMajorMedicalCarrierOptions(carriers, majorMedicalCarriersData);
  }, [benefitType, carriers, majorMedicalCarriersData]);

  const shouldDisable = isDisabled || isLoading;

  useEffect(() => {
    if (carrierOptions.length === 0 || !field.value || isEditable) {
      return;
    }
    const isCustomCarrier = !carrierOptions.includes(field.value);
    setIsEditMode(isCustomCarrier);
  }, [carrierOptions, field.value, setIsEditMode, isEditable]);

  return (
    <>
      {isEditMode ? (
        <Autocomplete
          {...field}
          size={size === 'sm' ? 'xs' : size === 'xl' ? 'lg' : size}
          required={isRequired}
          disabled={shouldDisable}
          placeholder={placeholder}
          label={label}
          data={carrierOptions}
        />
      ) : (
        <Select
          {...field}
          size={size === 'sm' ? 'xs' : size === 'xl' ? 'lg' : size}
          required={isRequired}
          disabled={shouldDisable}
          placeholder={placeholder}
          label={label}
          data={carrierOptions}
          allowDeselect={false}
          searchable
          onChange={(...changePayload) => {
            field.onChange(...changePayload);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendEvent('select_carrier', analyticsPayload);
          }}
        />
      )}

      {!isEditable && shouldShowNotListedCheckbox && (
        <Checkbox
          checked={isEditMode}
          onChange={(event) => {
            const checked = event.currentTarget.checked;
            setIsEditMode(checked);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendEvent('not_listed_carrier', { checked });
          }}
          label="Carrier not listed"
          disabled={shouldDisable}
          my="sm"
        />
      )}

      {shouldShowError && (
        <FormErrorMessage fieldName={formProps.name} errors={errors} />
      )}
    </>
  );
}
